import { useState, useEffect } from 'react';
import BlogItem from '../components/BlogItem/BlogItem';
import Tag from '../components/ui/Tag';

import '../styles/BlogPage.css';
import '../styles/global.css';

function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  // Fetch the articles data from public/articles.json
  useEffect(() => {
    fetch('/articles/articles.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch articles metadata: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => setArticles(data))
      .catch((error) => console.error('Error loading articles:', error));
  }, []);

  // Extract unique tags from articles
  const allTags = Array.from(new Set(articles.flatMap((article) => article.tags || [])));
  const languageTags = allTags.filter((tag) => ['english', 'french'].includes(tag));
  const topicTags = allTags.filter((tag) => !languageTags.includes(tag));

  // Filter articles based on search query or selected tag
  const sortedArticles = [...articles].sort((a, b) => new Date(b.date) - new Date(a.date));

  const filteredArticles = sortedArticles.filter((article) => {
    const title = article.title || '';
    const text = article.text || '';
    const tags = article.tags || [];

    return (
      (selectedTag ? tags.includes(selectedTag) : true) &&
      (selectedLanguage ? tags.includes(selectedLanguage) : true) &&
      (title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tags.some(
          (tag) => typeof tag === 'string' && tag.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    );
  });

  const hasActiveFilters = searchQuery || selectedTag || selectedLanguage;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTag(null);
    setSelectedLanguage(null);
  };

  return (
    <div className="page-wrapper">
      <div className="page">
        <section className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--accent-green)]">
            Writings
          </p>
          <h1 className="mt-2 text-3xl font-bold text-[var(--text-primary)] md:text-4xl">
            Articles and notes
          </h1>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-[var(--text-secondary)]">
            Notes on AI, software architecture, documentation, mathematics, and the craft around engineering.
          </p>

          <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
            <form className="w-full">
              <label htmlFor="article-search" className="sr-only">
                Search articles or tags
              </label>
              <input
                id="article-search"
                type="text"
                placeholder="Search articles or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-base text-[var(--text-primary)] outline-none transition placeholder:text-[var(--text-muted)] focus:border-[var(--accent-gold)] focus:ring-2 focus:ring-[var(--accent-gold)]/30"
              />
            </form>

            <div className="flex flex-wrap gap-2 lg:max-w-xl lg:justify-end">
              {topicTags.map((tag, idx) => (
                <Tag
                  key={idx}
                  active={selectedTag === tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          </div>

          {languageTags.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[var(--border-color)] pt-4">
              <span className="text-sm font-semibold text-[var(--text-secondary)]">Language</span>
              {languageTags.map((tag) => (
                <Tag
                  key={tag}
                  active={selectedLanguage === tag}
                  onClick={() => setSelectedLanguage(selectedLanguage === tag ? null : tag)}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          )}
        </section>

        <div className="mb-4 flex items-center justify-between gap-4 text-sm text-[var(--text-secondary)]">
          <span>{filteredArticles.length} article{filteredArticles.length === 1 ? '' : 's'}</span>
          {hasActiveFilters && (
            <button
              type="button"
              className="rounded-sm text-[var(--accent-gold)] transition hover:text-[var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>

        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 gap-6">
          {filteredArticles.map((article, index) => (
            <BlogItem
              key={article.fileName}
              fileName={article.fileName}
              title={article.title}
              text={article.text}
              image={article.image}
              date={article.date}
              tags={article.tags}
              isLatest={index === 0 && !hasActiveFilters}
            />
          ))}
          </div>
        ) : (
          <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] p-8 text-center shadow-sm">
            <h2 className="text-xl font-bold text-[var(--text-primary)]">No articles match this search.</h2>
            <p className="mt-2 text-base text-[var(--text-secondary)]">
              Try another keyword or clear the active filters.
            </p>
            <button
              type="button"
              className="mt-5 rounded-md border border-[var(--accent-gold)] px-4 py-2 text-sm font-semibold text-[var(--accent-gold)] transition hover:bg-[var(--accent-gold)] hover:text-neutral-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlogPage;
